import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Wallet, Eye, Filter, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/formatters';
import { SettlementFilterSidebar, SettlementFilterValues } from '@/components/features/financials/SettlementFilterSidebar';
import { GenerateTenantSettlementModal } from '@/components/features/financials/GenerateTenantSettlementModal';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface Settlement {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  shipment_value: number;
  delivery_fee: number;
  total_amount: number;
  currency: string;
  period_start: string;
  period_end: string;
  status: 'draft' | 'confirmed' | 'paid';
  created_at: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'draft':
      return 'bg-muted text-muted-foreground border-border';
    case 'confirmed':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'paid':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'pending':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'draft':
      return 'مسودة';
    case 'confirmed':
      return 'مؤكدة';
    case 'paid':
      return 'مدفوعة';
    case 'pending':
      return 'معلقة';
    default:
      return status;
  }
};

const initialFilters: SettlementFilterValues = {
  status: '',
  date_from: '',
  date_to: '',
};

export default function SettlementsList() {
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [filters, setFilters] = useState<SettlementFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);

  useEffect(() => {
    fetchSettlements();
  }, []);

  const fetchSettlements = async () => {
    try {
      const response: any = await get('/api/v1/dashboard/settlements');
      const data = response?.data || response || [];
      setSettlements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
    } finally {
      setHasFetched(true);
    }
  };

  const handleSettlementClick = (settlement: Settlement) => {
    navigate(`/dashboard/settlements/${settlement.id}`);
  };

  const handleFilterChange = (key: keyof SettlementFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  // Client-side filtering
  const filtered = settlements.filter((s) => {
    if (filters.status && s.status !== filters.status) return false;
    if (filters.date_from) {
      const from = new Date(filters.date_from);
      const created = new Date(s.created_at);
      if (created < from) return false;
    }
    if (filters.date_to) {
      const to = new Date(filters.date_to);
      to.setHours(23, 59, 59, 999);
      const created = new Date(s.created_at);
      if (created > to) return false;
    }
    return true;
  });

  const columns = [
    {
      header: 'الشريك',
      accessor: (row: Settlement) => row.tenant_name || row.tenant_id.slice(0, 8),
      className: 'font-medium',
    },
    {
      header: 'الفترة',
      accessor: (row: Settlement) =>
        `${formatDate(row.period_start)} - ${formatDate(row.period_end)}`,
    },
    {
      header: 'قيمة الشحنات',
      accessor: (row: Settlement) =>
        `${Number(row.shipment_value).toLocaleString()}`,
    },
    {
      header: 'رسوم التوصيل',
      accessor: (row: Settlement) => (
        <span className="text-blue-600 font-medium">
          {Number(row.delivery_fee).toLocaleString()}
        </span>
      ),
    },
    {
      header: 'الإجمالي',
      accessor: (row: Settlement) => (
        <span className="font-bold">
          {Number(row.total_amount).toLocaleString()}
        </span>
      ),
    },
    {
      header: 'الحالة',
      accessor: (row: Settlement) => (
        <Badge variant="outline" className={getStatusColor(row.status)}>
          {getStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      header: 'تاريخ الإنشاء',
      accessor: (row: Settlement) => formatDate(row.created_at),
      className: 'text-muted-foreground',
    },
  ];

  const renderActions = (row: Settlement) => (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      onClick={(e) => { e.stopPropagation(); handleSettlementClick(row); }}
      title="عرض التفاصيل"
    >
      <Eye className="h-4 w-4" />
    </Button>
  );

  const FilterSidebarContent = (
    <SettlementFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">التسويات المالية</h1>
              <p className="text-sm text-muted-foreground">تسويات العملاء والشركاء</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowCreate(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              إنشاء تسوية
            </Button>
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex gap-4 ">
        <div className="hidden lg:block">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {(!hasFetched || loading) ? (
            <LoadingSkeleton type="table" rows={10} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Wallet className="h-16 w-16" />}
              title="لا توجد تسويات"
              description="لم يتم إنشاء أي تسويات بعد. اضغط «إنشاء تسوية» لإنشاء تسوية جديدة لأحد الشركاء."
            />
          ) : (
            <DataTable
              data={filtered}
              columns={columns}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              actions={renderActions}
              emptyMessage="لا توجد تسويات"
            />
          )}
        </div>
      </div>

      <GenerateTenantSettlementModal
        open={showCreate}
        onOpenChange={setShowCreate}
        onSuccess={fetchSettlements}
      />
    </PageContainer>
  );
}
